import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import './LoginPage.css';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithMS365 } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Staff password login state
  const [staffEmail, setStaffEmail] = useState('');
  const [staffPassword, setStaffPassword] = useState('');
  const [showStaffPassword, setShowStaffPassword] = useState(false);
  const [staffLoginMode, setStaffLoginMode] = useState(false);

  // Only show dev login options on localhost
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Dev login state (staff)
  const [devMode, setDevMode] = useState(false);
  const [devEmail, setDevEmail] = useState('supp.lead@axslogic.com');
  const [devName, setDevName] = useState('Support Lead');
  const [devRole, setDevRole] = useState('SUPP_LEAD');

  // Dev login state (client)
  const [devClientMode, setDevClientMode] = useState(false);

  React.useEffect(() => {
    const token = searchParams.get('token');
    const refresh = searchParams.get('refresh');
    if (token && refresh) {
      localStorage.setItem('access_token', token);
      localStorage.setItem('refresh_token', refresh);
      window.location.href = '/dashboard';
    }
  }, [searchParams]);

  const handleClientLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    if (!staffEmail || !staffPassword) return;
    setLoading(true);
    try {
      const resp = await authAPI.staffLogin(staffEmail, staffPassword);
      const { access_token, refresh_token } = resp.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      toast.success('Welcome!');
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Staff login failed');
    } finally {
      setLoading(false);
    }
  };

  const devRolePresets = {
    ADMIN: { name: 'System Admin', email: 'admin@axslogic.com', groups: [] },
    CTO: { name: 'CTO (Admin)', email: 'cto@axslogic.com', groups: ['ITRK_CTO'] },
    SUPP_LEAD: { name: 'Support Lead', email: 'supp.lead@axslogic.com', groups: ['ITRK_SUPP', 'ITRK_SUPP_LEAD'] },
    ENGG_LEAD: { name: 'Engineering Lead', email: 'engg.lead@axslogic.com', groups: ['ITRK_ENGG', 'ITRK_ENGG_LEAD'] },
    QA_LEAD: { name: 'QA Lead', email: 'qa.lead@axslogic.com', groups: ['ITRK_QA', 'ITRK_QA_LEAD'] },
    ENGG1: { name: 'Engineer1', email: 'engg1@axslogic.com', groups: ['ITRK_ENGG'] },
    ENGG2: { name: 'Engineer2', email: 'engg2@axslogic.com', groups: ['ITRK_ENGG'] },
    QA1: { name: 'QA Analyst', email: 'qa1@axslogic.com', groups: ['ITRK_QA'] },
    QA2: { name: 'QA Analyst', email: 'qa2@axslogic.com', groups: ['ITRK_QA'] },
    OPS_LEAD: { name: 'DevOps Lead', email: 'ops.lead@axslogic.com', groups: ['ITRK_OPS', 'ITRK_OPS_LEAD'] },
  };

  const handleDevLogin = async () => {
    setLoading(true);
    try {
      const preset = devRolePresets[devRole] || devRolePresets.SUPP_LEAD;
      const resp = await authAPI.devLogin({
        email: devEmail, display_name: devName,
        role: devRole, groups: preset.groups,
      });
      const { access_token, refresh_token } = resp.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      toast.success(`Logged in as ${devName}`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dev login failed');
    } finally {
      setLoading(false);
    }
  };

  const devClientPresets = [
    { email: 'maria.santos@unionbank.ph', name: 'Maria Santos (UBP)', password: 'Welcome@123' },
    { email: 'james.reyes@unionbank.ph', name: 'James Reyes (UBP)', password: 'Welcome@123' },
  ];

  const handleDevClientLogin = async (preset) => {
    setLoading(true);
    try {
      await login(preset.email, preset.password);
      toast.success(`Logged in as ${preset.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Client login failed — ensure seed_client has been run');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-brand" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bg.png)` }}>
          <div className="login-brand-inner">
            <img src="/axslogic_logo.png" alt="Axslogic" className="login-logo-img" />
            <h1>Axslogic Support Portal</h1>
            <p>Client support portal for reporting issues and tracking resolution.</p>
          </div>
        </div>

        <div className="login-form-section">
          <div className="login-form-container">
            <h2>Sign in</h2>

            {/* Staff SSO */}
            <button className="btn-ms365" onClick={() => authAPI.ms365Login()} type="button">
              <svg width="20" height="20" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Axslogic staff — Sign in with Microsoft
            </button>

            {/* Staff password login toggle */}
            <button type="button" onClick={() => setStaffLoginMode(!staffLoginMode)}
              style={{ width: '100%', marginTop: 8, padding: '8px', background: 'none',
                border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
              {staffLoginMode ? 'Hide staff password login' : 'Axslogic staff — Sign in with password'}
            </button>

            {staffLoginMode && (
              <form onSubmit={handleStaffLogin} style={{ marginTop: 8, padding: 12, background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: 11, color: '#0369a1', marginBottom: 8 }}>Staff email + password login</div>
                <input type="email" className="form-input" value={staffEmail}
                  onChange={(e) => setStaffEmail(e.target.value)} placeholder="admin@axslogic.com"
                  required style={{ marginBottom: 6, fontSize: 13 }} />
                <div style={{ position: 'relative' }}>
                  <input type={showStaffPassword ? 'text' : 'password'} className="form-input"
                    value={staffPassword} onChange={(e) => setStaffPassword(e.target.value)}
                    placeholder="Password" required style={{ paddingRight: 40, fontSize: 13 }} />
                  <button type="button" onClick={() => setShowStaffPassword(!showStaffPassword)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, display: 'flex' }}
                    tabIndex={-1}>
                    {showStaffPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}
                  style={{ width: '100%', marginTop: 8 }}>
                  {loading ? 'Signing in...' : 'Sign in as staff'}
                </button>
              </form>
            )}

            {/* Dev mode staff */}
            {isDev && <button type="button" onClick={() => setDevMode(!devMode)}
              style={{ width: '100%', marginTop: 8, padding: '6px', background: 'none',
                border: '1px dashed #d1d5db', borderRadius: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>
              {devMode ? 'Hide dev login' : 'Dev mode (staff testing)'}
            </button>}

            {isDev && devMode && (
              <div style={{ marginTop: 8, padding: 12, background: '#fefce8', borderRadius: 6, border: '1px solid #fef08a' }}>
                <div style={{ fontSize: 11, color: '#92400e', marginBottom: 8 }}>Dev-only: creates a staff user without MS365</div>
                <input className="form-input" value={devEmail} onChange={(e) => setDevEmail(e.target.value)}
                  placeholder="Email" style={{ marginBottom: 6, fontSize: 13 }} />
                <input className="form-input" value={devName} onChange={(e) => setDevName(e.target.value)}
                  placeholder="Display name" style={{ marginBottom: 6, fontSize: 13 }} />
                <select className="form-select" value={devRole}
                        onChange={(e) => {
                          setDevRole(e.target.value);
                          const p = devRolePresets[e.target.value];
                          if (p) {
                            setDevName(p.name);
                            setDevEmail(p.email);
                          }
                        }}
                        style={{ marginBottom: 8, fontSize: 13 }}>
                  {Object.entries(devRolePresets).map(([k, v]) => (
                      <option key={k} value={k}>{v.name}</option>
                  ))}
                </select>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleDevLogin}
                  style={{ width: '100%' }} disabled={loading}>
                  {loading ? 'Logging in...' : `Login as ${devName}`}
                </button>
              </div>
            )}

            <div className="login-divider"><span>or sign in as client</span></div>

            {/* Client login */}
            <form onSubmit={handleClientLogin}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="your.email@company.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#9ca3af', padding: 4, display: 'flex',
                    }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-login" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            {/* Dev mode client */}
            {isDev && <button type="button" onClick={() => setDevClientMode(!devClientMode)}
              style={{ width: '100%', marginTop: 8, padding: '6px', background: 'none',
                border: '1px dashed #d1d5db', borderRadius: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>
              {devClientMode ? 'Hide client presets' : 'Dev mode (client testing)'}
            </button>}

            {isDev && devClientMode && (
              <div style={{ marginTop: 8, padding: 12, background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 11, color: '#166534', marginBottom: 8 }}>Quick login as seeded client user (UBP)</div>
                {devClientPresets.map((preset) => (
                  <button key={preset.email} type="button" className="btn btn-sm"
                    onClick={() => handleDevClientLogin(preset)} disabled={loading}
                    style={{ width: '100%', marginBottom: 4, fontSize: 12, justifyContent: 'flex-start', textAlign: 'left' }}>
                    {preset.name} — {preset.email}
                  </button>
                ))}
              </div>
            )}

            <p className="login-help">
              Need access? Contact Axslogic
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
